import styled from '@emotion/styled'
import { useMemo, useState } from 'react'

import { Dayjs } from '@bemlo/date'
import { ShiftHours } from '@bemlo/enums'
import { toArray } from '@bemlo/utils'

import { Row } from './Row'
import { ResourceAvailability, ShiftBooking } from './types'
import { Color, theme } from '../../../constants'
import { Div, Flex, Icon, IconName, Text } from '../../../elements'
import { useTranslations } from '../../../translate'
import { SHIFT_HOURS_STYLES } from '../../../utils/shift-hours-styles'

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'max-content 3fr 3fr 3fr 40px',
  gridAutoRows: 45,
  gridGap: theme.spacing[2],
})

const DAY_STYLES = SHIFT_HOURS_STYLES['DAY']
const EVENING_STYLES = SHIFT_HOURS_STYLES['EVENING']
const NIGHT_STYLES = SHIFT_HOURS_STYLES['NIGHT']

const ShiftTypeHeader = (props: {
  icon: IconName
  color: Color
  label: string
}) => {
  return (
    <Flex
      gap={1}
      items="center"
      styles={{ color: props.color, justifyContent: 'center', mr: 2 }}
    >
      <Icon name={props.icon} />
      <Text bold>{props.label}</Text>
    </Flex>
  )
}

const Header = () => {
  const t = useTranslations()

  return (
    <>
      <Div />
      <ShiftTypeHeader {...DAY_STYLES} label={t.shifts.day()} />
      <ShiftTypeHeader {...EVENING_STYLES} label={t.shifts.twilight()} />
      <ShiftTypeHeader {...NIGHT_STYLES} label={t.shifts.night()} />
      <Div />
    </>
  )
}

type ResourceAvailabilityProps = {
  month: Dayjs
  availability: ResourceAvailability[]
  bookings: ShiftBooking[]
  onCreateExactAvailability: (startDateTime: Dayjs, endDateTime: Dayjs) => void
  onUpdateExactAvailability: (
    id: string,
    startDateTime: Dayjs,
    endDateTime: Dayjs,
  ) => void
  onMarkAsAvailable: (day: Dayjs, shiftHours: ShiftHours) => void
  onMarkAsUnavailable: (id: string) => void
  onDelete: (id: string[]) => Promise<void>
}

export const ResourceAvailabilityCalendar = (
  props: ResourceAvailabilityProps,
) => {
  const [defaultTime, setDefaultTime] = useState({
    startTime: '07:00',
    endTime: '22:00',
  })

  const days = useMemo(() => {
    const daysInMonth = props.month.daysInMonth()
    const days = []
    for (let i = 0; i < daysInMonth; i++) {
      const availability = props.availability.filter(({ startDateTime }) =>
        props.month.add(i, 'day').isSame(startDateTime, 'day'),
      )
      const booking = props.bookings.find(({ startDateTime }) =>
        props.month.add(i, 'day').isSame(startDateTime, 'day'),
      )
      days.push({ day: props.month.add(i, 'day'), availability, booking })
    }
    return days
  }, [props.month, props.bookings, props.availability])

  return (
    <Grid>
      <Header />
      {days.map(({ day, availability, booking }) => (
        <Row
          key={day.format()}
          day={day}
          onCreateExactAvailability={props.onCreateExactAvailability}
          onUpdateExactAvailability={props.onUpdateExactAvailability}
          onDelete={(ids) => props.onDelete(toArray(ids))}
          onMarkAsAvailable={(shiftHours) =>
            props.onMarkAsAvailable(day, shiftHours)
          }
          onMarkAsUnavailable={props.onMarkAsUnavailable}
          availability={availability}
          defaultTimes={defaultTime}
          setDefaultTime={setDefaultTime}
          booking={booking}
        />
      ))}
    </Grid>
  )
}
